import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { AppButton, AppDrawer } from "@/components/app";
import Logo from "@/components/icons/Logo";
import { ROUTES } from "@/lib/routes";

export function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { label: "Features", href: "#features" },
    { label: "How It works", href: "#how-it-works" },
    { label: "Testimonials", href: "#testimonials" },
    { label: "FAQ", href: "#faq" },
  ];

  // Handle scroll to section after navigation
  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  }, [location]);

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string,
  ) => {
    e.preventDefault();
    setIsOpen(false);

    // If we're not on home page, navigate to home first
    if (location.pathname !== ROUTES.HOME) {
      navigate(ROUTES.HOME + href);
    } else {
      // If already on home page, just scroll
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  return (
    <header className="">
      <div className="wrapper">
        <div className="xl:py-[50px] lg:py-10 py-4 md:py-8">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <div className="w-[172px] lg:w-[230px]">
                <Logo />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center xl:gap-10 gap-4">
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className={`relative text-foreground hover:opacity-70 transition-colors text-lg font-semibold pb-1 
                    after:content-[''] after:absolute after:bottom-0 after:left-0 after:h-[2px] after:bg-primary 
                    after:transition-all after:duration-300 after:ease-out
                    ${
                      location.hash === item.href
                        ? "after:w-full"
                        : "after:w-0 hover:after:w-full"
                    }`}
                >
                  {item.label}
                </a>
              ))}
            </nav>

            {/* Desktop CTA Buttons */}
            <div className="hidden lg:flex items-center gap-3">
              <AppButton variant="outlined" size="lg" asChild>
                <Link to={ROUTES.CONTACT_US}>Contact Us</Link>
              </AppButton>
              {/* <AppButton size="lg" asChild>
                <Link to={'/contanct'}>Download App</Link>
              </AppButton> */}
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden">
              <AppButton
                onClick={() => setIsOpen(!isOpen)}
                aria-label="Toggle menu"
                size="icon"
              >
                {isOpen ? <X size={24} /> : <Menu size={24} />}
              </AppButton>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Drawer */}
        <AppDrawer
          open={isOpen}
          onClose={() => setIsOpen(false)}
          direction="right"
          size={320}
        >
          <div className="p-6">
            {/* Header with Logo and Close Button */}
            <div className="flex items-center justify-between mb-8">
              <div className="w-[115px] md:w-[172px] lg:w-[230px]">
                <Logo />
              </div>
              <AppButton
                onClick={() => setIsOpen(false)}
                aria-label="Close menu"
                size="icon"
                variant="text"
              >
                <X size={24} />
              </AppButton>
            </div>

            {/* Navigation Links */}
            <nav className="flex flex-col gap-2 mb-6">
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className={`text-foreground hover:text-primary transition-all text-base font-medium px-5 py-3 bg-muted shadow-sm rounded-lg duration-150 ${
                    location.hash === item.href
                      ? "bg-primary text-white"
                      : "hover:bg-primary hover:text-white"
                  }`}
                >
                  {item.label}
                </a>
              ))}
            </nav>

            {/* CTA Buttons */}
            <div className="flex flex-col gap-3 pt-6 border-t border-border-light">
              <AppButton variant="outlined" size="lg" fullWidth asChild>
                <Link to={ROUTES.CONTACT_US} onClick={() => setIsOpen(false)}>
                  Contact Us
                </Link>
              </AppButton>
              {/* <AppButton size="lg" fullWidth asChild>
                <Link to={"/contanct"} onClick={() => setIsOpen(false)}>
                  Download App
                </Link>
              </AppButton> */}
            </div>
          </div>
        </AppDrawer>
      </div>
    </header>
  );
}
